import Badge from '@pelckmans/elementis/components/badge';
import Button from '@pelckmans/elementis/components/button';
import Section from '@pelckmans/elementis/components/section';
import { Cell } from '@pelckmans/elementis/components/table/cell';
import FilterOffIcon from '@pelckmans/elementis/icons/filter-off';
import FilterOnIcon from '@pelckmans/elementis/icons/filter-on';
import { RefObject, useContext, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import ModalContext from '../../../../contexts/ModalContext';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import useLicenses from '../../../../hooks/useLicenses';
import LoadingState from '../../../../shared-components/LoadingState';
import ModuleSelectionField from '../../../../shared-components/ModuleSelectionField';
import { groupBelongsToSelectedSchoolYear } from '../utils/groupBelongsToSelectedSchoolYear';
import { HorizontalScrollContextProvider } from '../../../../shared-components/table/context/HorizontalScrollContext';
import { useFilters } from '../context/filter-context/FilterContext';
import { useResultsDataContext } from '../context/ResultsDataContext';
import FilterStudentsModal, { MODAL_ID } from '../../../../shared-components/filter-students-modal/FilterStudentsModal';
import SchoolFilter from './exercise/filters/SchoolFilter';
import SchoolYearFilter from './exercise/filters/SchoolYearFilter';
import { useScorableItemTOCHierarchy } from './exercise/hooks/useScorableItemTOCHierarchy';
import useTableData from './exercise/hooks/useTableData';
import ExportButton from './export-button/ExportButton';
import ResultsEmptyState from './ResultsEmptyState';
import ScorableItemFilterModal from './scorable-item-filter-modal/ScorableItemFilterModal';
import ResultsTable from './table/ResultsTable';

function ExerciseTab({
  containerRef,
  tableRef,
}: {
  containerRef: RefObject<HTMLDivElement>;
  tableRef: RefObject<HTMLTableElement>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isDeeplink = location.key === 'default';
  const { isModuleLicensed } = useLicenses();
  const id = useId();

  useDocumentTitle(t('PAGES.RESULTS.PER_EXERCISE.DOCUMENT_TITLE'));

  const {
    selectedModule,
    selectedSchoolYear,
    selectedSchool,
    selectedGroups,
    selectedMembers,
    selectedScorableItems,
    setSelectedModule,
    setSelectedMembers,
    setSelectedScorableItems,
    clearFilters,
    spScorableItemIds,
    spMemberIdsPerGroup,
    usedLocalStorageFilters,
    determiningFilters,
  } = useFilters();

  const isDetailView = spScorableItemIds.length === 1;

  const {
    loading,
    data,
    noGroups,
    noScorableItems,
    noGroupsForFilter,
    noScorableItemsForFilter,
    resultsWithGroup,
    exportData,
    fetchScoreData,
  } = useTableData({ detailView: isDetailView });

  const { tocAndScorableItems } = useScorableItemTOCHierarchy();

  const { showModal } = useContext(ModalContext);
  const { members, groups, modules } = useResultsDataContext();

  const renderTable = () => {
    if (!selectedModule || loading || determiningFilters) {
      return <LoadingState variant="table" aria-label={t('PAGES.RESULTS.PER_EXERCISE.LOADING_STATE.A11Y_LABEL')} />;
    }

    if (isUnlicensed) {
      return <ResultsEmptyState variant="no-license" id={id} />;
    }

    if (noGroups) return <ResultsEmptyState variant="no-groups" id={id} />;
    if (noGroupsForFilter) return <ResultsEmptyState variant="no-groups-filter" id={id} />;
    if (noScorableItems) {
      return <ResultsEmptyState variant="no-scorable-items" moduleNiceName={selectedModule.niceName} id={id} />;
    }
    if (noScorableItemsForFilter || data.rows.length === 0) {
      return <ResultsEmptyState variant="no-scorable-items-filter" onClear={clearFilters} id={id} />;
    }

    function handleSetSelectedMembers(selectedMembersPerGroup: Record<string, string[]>) {
      const concatenatedIds = Object.entries(selectedMembersPerGroup)
        .map(([groupId, memberIds]) => {
          return memberIds.map(memberId => `${groupId};${memberId}`);
        })
        .flat();
      setSelectedMembers(concatenatedIds);
    }

    const membersFilterCount = spMemberIdsPerGroup
      ? Object.values(spMemberIdsPerGroup).reduce((acc, members) => acc + members.length, 0)
      : 0;
    const membersFilterActive = membersFilterCount > 0;

    return (
      <ResultsTable
        data={data}
        ref={tableRef}
        detailView={isDetailView}
        cellHeaderActions={
          <Cell.HeaderText.Actions>
            <Button
              prominence="neutral"
              onClick={() =>
                showModal(
                  <FilterStudentsModal
                    id={MODAL_ID}
                    groups={groups.filter(
                      g =>
                        g.acceptedMemberCount > 0 &&
                        groupBelongsToSelectedSchoolYear(g, selectedSchoolYear?.id) &&
                        g.school.id === selectedSchool?.id,
                    )}
                    translationKeyBase="PAGES.RESULTS.PER_EXERCISE.MODALS.FILTER_STUDENTS"
                    selectedGroups={selectedGroups}
                    membersPerGroup={members}
                    // No filter set means selectedMembers holds every member, the modal wants the actual selection
                    selectedMembersPerGroup={spMemberIdsPerGroup ? selectedMembers : {}}
                    setSelectedMembers={handleSetSelectedMembers}
                  />,
                )
              }
              startIcon={<FilterOnIcon data-testid="filter-members" />}
              badge={
                membersFilterActive ? (
                  <Badge prominence="ghost" size="sm">
                    {membersFilterCount}
                  </Badge>
                ) : undefined
              }
            >
              {t('ACTIONS.FILTER')}
            </Button>
            {membersFilterActive && (
              <Button
                startIcon={<FilterOffIcon data-testid="filter-off-icon" />}
                aria-label={t('PAGES.RESULTS.PER_EXERCISE.FILTERS.STUDENTS.A11Y_LABEL')}
                tooltip={t('PAGES.RESULTS.PER_EXERCISE.FILTERS.STUDENTS.TOOLTIP')}
                onClick={() => setSelectedMembers([])}
                key="clear-students-filter"
                prominence="neutral"
              />
            )}
          </Cell.HeaderText.Actions>
        }
        id={id}
      />
    );
  };

  const isUnlicensed = !isModuleLicensed(selectedModule);

  const baseSectionTitle = t('PAGES.RESULTS.PER_EXERCISE.SECTION_TITLE');
  const sectionTitle = isDetailView ? `${baseSectionTitle}: ${selectedScorableItems[0]?.name}` : baseSectionTitle;

  const hideFilters = noGroups || isDetailView || loading;
  const scorableItemFilterActive = spScorableItemIds.length > 0;

  return (
    <Section
      title={sectionTitle}
      actionGroupAriaLabel={undefined}
      actionGroupRole={undefined}
      {...(!isDeeplink && isDetailView
        ? {
            onBackClick: () => {
              if (usedLocalStorageFilters) {
                setSelectedScorableItems([]);
              } else {
                navigate(-1);
              }
            },
            backButtonLabel: t('PAGES.RESULTS.BACK_BUTTON_A11Y_LABEL'),
          }
        : {})}
      filter={
        hideFilters ? undefined : (
          <>
            <ModuleSelectionField
              key="module-filter"
              modules={modules}
              selectedModule={selectedModule}
              setSelectedModule={setSelectedModule}
              ariaLabel={t('PAGES.RESULTS.FILTERS.MODULE_FILTER.A11Y_LABEL')}
              dropdownMenuAriaLabel={t('PAGES.RESULTS.FILTERS.MODULE_FILTER.DROPDOWN_MENU.A11Y_LABEL')}
            />
            <SchoolYearFilter key="school-year-filter" />
            <SchoolFilter key="school-filter" />
          </>
        )
      }
      filterActions={
        hideFilters ? undefined : (
          <>
            <Button
              key="open-scorable-item-filter"
              startIcon={<FilterOnIcon />}
              size="sm"
              prominence="neutral"
              disabled={isUnlicensed || tocAndScorableItems.length === 0}
              onClick={() =>
                showModal(
                  <ScorableItemFilterModal
                    id="scorable-item-filter-modal"
                    onConfirm={setSelectedScorableItems}
                    tableDataGroups={tocAndScorableItems}
                    selectedItemIds={spScorableItemIds}
                  />,
                )
              }
              badge={
                scorableItemFilterActive ? (
                  <Badge prominence="ghost" size="sm">
                    {spScorableItemIds.length}
                  </Badge>
                ) : undefined
              }
            >
              {t('PAGES.RESULTS.PER_EXERCISE.FILTERS.SCORABLE_ITEM.OPEN_MODAL_BUTTON')}
            </Button>
            {scorableItemFilterActive && (
              <Button
                startIcon={<FilterOffIcon data-testid="filter-off-icon" />}
                aria-label={t('PAGES.RESULTS.PER_EXERCISE.FILTERS.SCORABLE_ITEM.A11Y_LABEL')}
                tooltip={t('PAGES.RESULTS.PER_EXERCISE.FILTERS.SCORABLE_ITEM.TOOLTIP')}
                onClick={() => setSelectedScorableItems([])}
                key="clear-scorable-item-filter"
                prominence="neutral"
              />
            )}
          </>
        )
      }
      actions={
        !isUnlicensed &&
        !noScorableItems &&
        !loading && <ExportButton xlsxData={exportData} fetchData={fetchScoreData} results={resultsWithGroup} />
      }
      className="results-exercise-tab"
      contentClassName="content-wrapper"
      sectionLabelId={id}
    >
      <div className="scroll-table-container" ref={containerRef}>
        {renderTable()}
      </div>
    </Section>
  );
}

export default function WrappedExerciseTab() {
  const containerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  return (
    <HorizontalScrollContextProvider containerRef={containerRef} tableRef={tableRef}>
      <ExerciseTab containerRef={containerRef} tableRef={tableRef} />
    </HorizontalScrollContextProvider>
  );
}
